/**
 * Responsibility: show what was measured, and say plainly what was not.
 *
 * **The skipped list is not an afterthought.** "No defect found" and "that
 * could not be assessed" produce the same empty findings list and mean
 * opposite things, so a page that renders only findings is misleading exactly
 * when it matters most - on a card it could not see properly.
 */

import { Alert, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

import type { ScanResult } from '../../api/client';

/** Reasons the analysis declined, in language that says what to do about it. */
const WHY: Record<string, string> = {
  no_printed_frame:
    'This card has no plain printed border - full-art, holographic, or artwork bled to the edge. There is no frame to measure against.',
  front_border_not_uniform:
    'Whitening is measured on backs only. A holo front border changes lightness by more than a defect does, as the foil catches the light.',
  resolution_too_low: 'The photograph is not detailed enough for this measurement.',
  needs_raking_light: 'Needs a lamp low to the side, with the overheads off.',
  needs_edge_on_view: 'Needs a photograph of the card edge, side on.',
  card_not_detected: 'No card was found in the photograph.',
  border_not_uniform: 'The border is not uniform enough to use as its own reference.',
  band_fully_glared: 'Glare covers the border. Move the light or the card.',
  band_too_small: 'The card is too small in the frame to measure its border.',
};

export function ScanFindings({
  result,
  showCondition,
}: {
  result: ScanResult;
  showCondition: boolean;
}) {
  if (!result.detected) {
    return (
      <Alert severity="warning">
        No card found ({result.detect_reason}). Fill more of the frame and keep the
        background plain.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Chip size="small" label={`${result.px_per_mm} px/mm`} />
          <Chip size="small" label={result.face} variant="outlined" />
          {result.resolution_supports.map((s) => (
            <Chip key={s} size="small" color="success" variant="outlined" label={s} />
          ))}
        </Stack>

        {showCondition && (
          <>
            <Divider />
            <Typography variant="subtitle2">Centering</Typography>
            {result.centering.measurable ? (
              <Stack spacing={0.5}>
                <Typography variant="body2">
                  Left/right {(100 - result.centering.left_right).toFixed(0)}/
                  {result.centering.left_right.toFixed(0)} &middot; top/bottom{' '}
                  {(100 - result.centering.top_bottom).toFixed(0)}/
                  {result.centering.top_bottom.toFixed(0)} &mdash;{' '}
                  <strong>{result.centering.grade}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Borders: L {result.centering.left_mm} &middot; R {result.centering.right_mm}{' '}
                  &middot; T {result.centering.top_mm} &middot; B {result.centering.bottom_mm} mm
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {WHY[result.centering.reason] ?? result.centering.reason}
              </Typography>
            )}

            <Divider />
            <Typography variant="subtitle2">Border marks</Typography>
            {!result.border_measurable ? (
              <Typography variant="body2" color="text.secondary">
                {WHY[result.border_reason] ?? result.border_reason}
              </Typography>
            ) : result.findings.length === 0 ? (
              <Typography variant="body2" color="success.main">
                Nothing found on the border at this resolution.
              </Typography>
            ) : (
              <Stack spacing={0.5}>
                {result.findings.map((f, i) => (
                  <Typography key={`${f.region}-${i}`} variant="body2">
                    {f.region.replace(/_/g, ' ')} &mdash; {f.area_mm2} mm2, {f.severity}{' '}
                    (confidence {f.confidence})
                  </Typography>
                ))}
              </Stack>
            )}
          </>
        )}

        {result.skipped.length > 0 && (
          <>
            <Divider />
            <Typography variant="subtitle2">Not assessed</Typography>
            <Stack spacing={0.5}>
              {result.skipped.map(([what, why]) => (
                <Typography key={what} variant="caption" color="text.secondary">
                  <strong>{what.replace(/_/g, ' ')}</strong> &mdash; {WHY[why] ?? why}
                </Typography>
              ))}
            </Stack>
          </>
        )}

        <Alert severity="info" icon={false}>
          No grade is predicted, deliberately. These are measurements; what they are
          worth is a judgement, and a single number would be the most misused figure
          here.
        </Alert>
      </Stack>
    </Paper>
  );
}
